import { Injectable, inject } from '@angular/core';
import { ChunkContext } from '@app/shared/interfaces/chunk/chunk-context.interface';
import { ToastrService } from 'ngx-toastr';
import { ModuleRegistryEntry, DependencyAnalysis } from '../interfaces/python.interface';
import { PyodideFileJournal } from '../journal/python-journal.helper';
import { STEPS_NAMESPACE } from '../../../constants/general.constants';

@Injectable({
  providedIn: 'root',
})
export class PythonModuleManager {
  private toastr = inject(ToastrService);

  public moduleRegistry: Map<string, ModuleRegistryEntry> = new Map();
  private pythonRuntime: any;
  private jsExternalModulesNamespaces: any;
  private moduleCounter: number = 0;
  private isInitialized: boolean = false;
  private baseNamespace: string = STEPS_NAMESPACE;
  private fileJournal?: PyodideFileJournal;

  async initialize(
    runtime: any,
    jsExternalModulesNamespaces: any,
    journal?: PyodideFileJournal
  ) {
    if (this.isInitialized) {
      return;
    }

    this.pythonRuntime = runtime;
    this.jsExternalModulesNamespaces = jsExternalModulesNamespaces;
    this.fileJournal = journal;

    await this.setupModuleSystem();

    this.isInitialized = true;
  }

  /**
   * Check if module_system is initialized in Python runtime
   */
  isModuleSystemReady(): boolean {
    try {
      if (!this.pythonRuntime) {
        return false;
      }
      const result = this.pythonRuntime.runPython(`
'module_system' in globals() and hasattr(module_system, 'register_module')
      `);
      return result === true;
    } catch {
      return false;
    }
  }

  async setupModuleSystem() {
    const moduleSystemSetup = `
import sys
import io
import asyncio
import inspect
import traceback
import re
import ast
import json
from types import ModuleType
from contextlib import contextmanager

def format_error_message(error: Exception) -> str:
    """
    Format error messages in a clean, readable way.
    Removes verbose Pyodide/JS exception details and keeps only the essential error.
    """
    error_str = str(error)

    # If it's a JsException, extract the actual error message
    if 'JsException' in error_str or hasattr(error, '__class__') and 'JsException' in str(error.__class__):
        # Try to extract the actual error message
        # Pattern: "Error: <actual message>"
        match = re.search(r'Error:\\s*(.+?)(?:\\n|$)', error_str)
        if match:
            return match.group(1)

    # For other errors, just return the string representation
    return error_str

def format_traceback_concise(exc: Exception) -> str:
    """
    Format traceback in a concise way, showing only relevant information.
    Removes internal Pyodide frames and focuses on user code.
    """
    tb_lines = traceback.format_exception(type(exc), exc, exc.__traceback__)

    # Filter out internal Pyodide frames
    filtered_lines = []
    skip_next = False

    for line in tb_lines:
        # Skip internal Pyodide frames
        if 'pyodide' in line.lower() and ('ffi' in line.lower() or 'internal' in line.lower()):
            skip_next = True
            continue
        if skip_next and (line.startswith('  ') or line.startswith('    ')):
            continue
        skip_next = False
        filtered_lines.append(line)

    # If we filtered everything, return just the error message
    if len(filtered_lines) <= 1:
        return format_error_message(exc)

    return ''.join(filtered_lines).strip()

def _handle_system_exit(e, buffer, namespace, result):
    """Handle sys.exit() / SystemExit — inherits from BaseException, not Exception."""
    exit_code = e.code if e.code is not None else 0
    captured = buffer.getvalue() if buffer else ''
    if captured:
        result['output'] = (result.get('output') or '') + captured
    if exit_code != 0:
        return {
            'output': result.get('output', ''),
            'namespace': namespace,
            'error': f"Execution terminated with exit code {exit_code}"
        }
    if not result.get('output'):
        result['output'] = 'Code executed successfully'
    result['namespace'] = namespace
    return result

class DataObject(dict):
    """A dictionary that supports attribute-style access"""
    def __getattr__(self, key):
        try:
            return self[key]
        except KeyError:
            raise AttributeError(f"'DataObject' object has no attribute '{key}'")

    def __setattr__(self, key, value):
        self[key] = value

    def __delattr__(self, key):
        try:
            del self[key]
        except KeyError:
            raise AttributeError(f"'DataObject' object has no attribute '{key}'")

class UnifiedPythonSystem:
    """
    A unified system for managing Python module registration, execution, and output capture.
    Supports both synchronous and asynchronous code execution with proper module handling.
    """

    # Names that the Pyodide bridge registers on __main__ via python.globals.set()
    # (see PythonEHQBridge.registerJsExternalModules in python-ehq-bridge.helper.ts:755).
    # Each step's exec namespace is a fresh dict that does NOT inherit from __main__,
    # so these must be explicitly re-injected per run by setup_base_namespace. Kept in
    # sync with enoch's module-system.ts equivalent for runner parity.
    _BRIDGE_INJECTED_NAMES = (
        'getJobArguments',
        'BOX', 'box',
        'EHQ', 'ehq',
        'ARROW_FLIGHT', 'arrowFlight', 'arrow',
        'convert',
    )

    def __init__(self, base_namespace="steps"):
        """Initialize the Python system with necessary storage and output handling."""
        # Namespace configuration
        self._base_namespace = base_namespace

        # Core storage for module management
        self._modules = {}        # Stores ModuleType instances
        self._module_code = {}    # Stores original source code for each module
        self._dependencies = {}   # Tracks inter-module dependencies
        self._exports = {}        # Tracks exported symbols for each module
        self._executed_modules = set()  # Tracks which modules have been fully executed
        self._module_contexts = {} # Stores module context data

        # Output handling setup
        self._original_stdout = sys.stdout
        self._original_stderr = sys.stderr

    def store_module_context(self, module_id: str, context: dict) -> str:
        context_id = f"ctx_{module_id}"
        self._module_contexts[context_id] = context
        return context_id

    def get_module_context(self, context_id: str) -> dict:
        return self._module_contexts.get(context_id)

    def setup_base_namespace(self, namespace=None):
        if namespace is None:
            namespace = {}

        # Ensure data in globals is a DataObject for dot notation access
        # This wraps the data dict in-place so GUI modifications are preserved
        if 'data' in globals():
            if not isinstance(globals()['data'], DataObject):
                # Wrap existing data dict with DataObject in globals
                globals()['data'] = DataObject(globals()['data'])
        else:
            # Initialize empty DataObject if no data exists
            globals()['data'] = DataObject({})

        # Add essential items
        namespace.update({
            '__builtins__': __builtins__,
            'asyncio': asyncio,
            'data': globals()['data']  # Reference the same object, not a copy
        })

        # Add bridge-registered globals from the Pyodide bridge (__main__) or
        # this module's globals into the step execution namespace.
        #
        # getJobArguments has always been injected this way. BOX/box and EHQ/ehq
        # are bridge-registered service handlers (see PythonEHQBridge.registerJsExternalModules
        # in python-ehq-bridge.helper.ts:755), which uses two complementary calls:
        #   1. python.globals.set(name, handler)  -- adds to __main__.__dict__ immediately
        #   2. python.registerJsModule(name, ...) -- makes the name importable, but does
        #      NOT populate sys.modules until the first explicit import happens
        # So at step-execution time, the handlers are reliably visible on __main__.__dict__,
        # while sys.modules only contains them if a previous step (or this step's code)
        # already triggered an explicit import of BOX or EHQ. The sys.modules sweep below
        # therefore can't be relied on for first-call resolution -- we have to fish from
        # __main__ explicitly. Without this injection, step code that references BOX/EHQ
        # raises NameError because the step's exec namespace is a fresh dict that does
        # not inherit from __main__.
        _main = sys.modules.get('__main__')
        for _bridge_name in self._BRIDGE_INJECTED_NAMES:
            _bridge_val = None
            if _main is not None and getattr(_main, '__dict__', None) and _bridge_name in _main.__dict__:
                _bridge_val = _main.__dict__[_bridge_name]
            elif _bridge_name in globals():
                _bridge_val = globals()[_bridge_name]
            if _bridge_val is not None:
                namespace[_bridge_name] = _bridge_val

        # Add steps package to namespace
        if self._base_namespace in sys.modules:
            namespace[self._base_namespace] = sys.modules[self._base_namespace]

        # Add only executed modules
        for qualified_name, module in self._modules.items():
            if qualified_name in self._executed_modules:
                module_id = qualified_name.replace(f"{self._base_namespace}.", '')
                namespace[module_id] = module

        return namespace

    def _ensure_steps_package(self):
        """Ensures steps package exists and returns it"""
        if self._base_namespace not in sys.modules:
            steps_pkg = ModuleType(self._base_namespace)
            steps_pkg.__package__ = self._base_namespace
            steps_pkg.__path__ = []
            steps_pkg.__file__ = f"<virtual_{self._base_namespace}>"
            steps_pkg.__all__ = []
            sys.modules[self._base_namespace] = steps_pkg
            # print(f"Created steps namespace package")
        return sys.modules[self._base_namespace]

    @contextmanager
    def _capture_output(self):
        """
        Context manager for capturing stdout and stderr output.
        Creates new StringIO for each capture to avoid output accumulation.
        """
        # Create new buffer for each capture
        output_buffer = io.StringIO()

        # Store original stdout/stderr
        original_stdout = sys.stdout
        original_stderr = sys.stderr

        # Redirect output to new buffer
        sys.stdout = output_buffer
        sys.stderr = output_buffer

        try:
            yield output_buffer
        finally:
            # Restore original stdout/stderr
            sys.stdout = original_stdout
            sys.stderr = original_stderr
            # Close buffer
            output_buffer.close()

    def _format_result_for_display(self, result):
        """Format result for display, handling special cases like numpy arrays"""
        try:
            # Check if it's a numpy array
            if hasattr(result, '__class__') and 'numpy' in str(type(result)):
                # Use numpy's string representation which formats nicely
                return repr(result)
            # Check if it's a list, tuple, or other sequence
            elif isinstance(result, (list, tuple)):
                return str(result)
            # For dictionaries, use a pretty format
            elif isinstance(result, dict):
                return str(result)
            # For other objects, try repr first (more informative), fallback to str
            else:
                try:
                    return repr(result)
                except:
                    return str(result)
        except Exception:
            return str(result)

    async def register_module(self, module_id: str, name: str, code: str):
        """Register module without executing code"""
        try:
            qualified_name = f"{self._base_namespace}.{module_id}"
            # print(f"\\n=== Registering Module ===")
            # print(f"Module ID: {module_id}")
            # print(f"Qualified name: {qualified_name}")

            # Cleanup first
            self._cleanup_module(qualified_name)

            # Create module instance
            module_obj = ModuleType(qualified_name)
            module_obj.__file__ = f"<virtual_module_{module_id}>"
            module_obj.__package__ = self._base_namespace
            module_obj.__name__ = qualified_name

            # Setup initial namespace
            namespace = self.setup_base_namespace()
            module_obj.__dict__.update(namespace)

            # Register in sys.modules
            sys.modules[qualified_name] = module_obj

            # Setup steps package
            steps_pkg = self._ensure_steps_package()
            setattr(steps_pkg, module_id, module_obj)
            if module_id not in steps_pkg.__all__:
                steps_pkg.__all__.append(module_id)

            # Save module info
            self._modules[qualified_name] = module_obj
            self._module_code[qualified_name] = code

            # print(f"=== Module Registration Complete ===\\n")

            context = {
                'id': qualified_name,
                'module_obj': module_obj,
                'steps_pkg': steps_pkg,
                'namespace': module_obj.__dict__,
            }

            context_id = self.store_module_context(module_id, context)

            return {
                'id': qualified_name,
                'context_id': context_id,
                'is_loaded': True
            }

        except Exception as e:
            error_msg = f"Module registration failed: {format_error_message(e)}"
            print(error_msg)
            self._cleanup_module(qualified_name)
            raise RuntimeError(error_msg)

    async def execute_code(self, code: str, namespace=None, context_id=None):
        """
        Execute Python code with fresh output capture for each execution.
        Handles both synchronous and asynchronous code.
        Captures the result of the last expression.
        """
        try:
            if namespace is None:
                namespace = {}
            namespace = self.setup_base_namespace(namespace)

            # Initialize result structure
            result = {
                'output': '',
                'namespace': namespace,
                'error': None,
                'exports': [],
                'last_expression_result': None
            }

            # Extract top-level imports using ast to avoid matching inside docstrings/strings
            import_lines = []
            try:
                _tree = ast.parse(code)
                for _node in ast.iter_child_nodes(_tree):
                    if isinstance(_node, (ast.Import, ast.ImportFrom)):
                        _seg = ast.get_source_segment(code, _node)
                        if _seg:
                            import_lines.append(_seg)
            except SyntaxError:
                pass  # Skip import pre-extraction; errors will surface during main exec

            # Execute imports with fresh output capture
            if import_lines:
                import_code = '\\n'.join(import_lines)

                with self._capture_output() as buffer:
                    try:
                        exec(import_code, namespace)
                        import_output = buffer.getvalue()
                        if import_output:
                            result['output'] += import_output
                    except SystemExit as e:
                        return _handle_system_exit(e, buffer, namespace, result)
                    except Exception as e:
                        error_msg = format_error_message(e)
                        return {
                            'output': buffer.getvalue(),
                            'namespace': namespace,
                            'error': f"Import error: {error_msg}"
                        }

            # Execute main code with fresh output capture
            # Use original code to preserve all comments and formatting
            main_code = code
            if not main_code.strip():
                if not result['output']:
                    result['output'] = 'Code executed successfully'
                return result

            is_async = 'async' in main_code or 'await' in main_code

            # Check if the last line is an expression (not an assignment or statement)
            # Keep original lines with indentation, but ignore trailing comments when detecting the last line
            all_lines = main_code.split('\\n')
            last_non_comment_index = -1
            for i in range(len(all_lines) - 1, -1, -1):
                stripped = all_lines[i].strip()
                if not stripped:
                    continue
                if stripped.startswith('#'):
                    continue
                last_non_comment_index = i
                break
            # Get original and stripped last line
            original_last_line = all_lines[last_non_comment_index] if last_non_comment_index >= 0 else ''
            last_line = original_last_line.strip()

            # Check if line is indented (simple check: original != stripped)
            is_indented = original_last_line != last_line

            # Determine if last line is an expression that should return a value
            is_last_line_expression = (
                last_line and
                last_line not in ('"""', "'''", '""', "''") and  # Closing quotes of multiline strings
                not is_indented and  # Indented lines are part of blocks, not standalone expressions
                not last_line.startswith('#') and  # Exclude comments
                not last_line.startswith(('import ', 'from ', 'def ', 'class ', 'if ', 'for ', 'while ', 'try:', 'with ', 'async def')) and
                not ('=' in last_line and not any(op in last_line for op in ['==', '!=', '<=', '>=', '+=', '-=', '*=', '/='])) and
                not last_line.endswith(':') and
                not last_line.startswith(('print(', 'return ', 'yield ', 'raise ', 'assert ', 'del ', 'pass', 'break', 'continue'))
            )

            if is_async:
                import ast as _ast

                if is_last_line_expression:
                    # Separate the last expression from the rest
                    # Use all_lines to preserve indentation, find index of last non-comment line
                    last_non_empty_index = last_non_comment_index

                    main_statements = '\\n'.join(all_lines[:last_non_empty_index]) if last_non_empty_index > 0 else ''
                    last_expression = all_lines[last_non_empty_index] if last_non_empty_index >= 0 else ''

                    exec_code = (main_statements + '\\n' if main_statements else '') + '__last_result = ' + last_expression
                else:
                    exec_code = main_code

                # Capture print() output synchronously via StringIO, same as sync path.
                # Pyodide batched stdout does NOT deliver after execution returns.
                with self._capture_output() as buffer:
                    try:
                        code_obj = compile(exec_code, '<step>', 'exec', flags=_ast.PyCF_ALLOW_TOP_LEVEL_AWAIT)
                        coro = eval(code_obj, namespace)
                        if coro is not None:
                            await coro

                        captured_output = buffer.getvalue()
                        if captured_output:
                            result['output'] += captured_output

                        if is_last_line_expression:
                            execution_result = namespace.get('__last_result')
                        else:
                            execution_result = None

                        if is_last_line_expression and execution_result is not None:
                            # Skip void bridge call results (JsProxy wrapping a Promise)
                            from pyodide.ffi import JsProxy
                            is_void_bridge_result = (
                                isinstance(execution_result, JsProxy) and
                                hasattr(execution_result, 'then')
                            )
                            if not is_void_bridge_result:
                                result['last_expression_result'] = execution_result
                                formatted_result = self._format_result_for_display(execution_result)
                                if captured_output:
                                    result['output'] += '\\n' + formatted_result
                                else:
                                    result['output'] = formatted_result
                        elif not result['output']:
                            result['output'] = 'Code executed successfully'

                        result['namespace'] = namespace
                    except SystemExit as e:
                        return _handle_system_exit(e, buffer, namespace, result)
                    except Exception as e:
                        error_msg = format_error_message(e)
                        return {
                            'output': buffer.getvalue(),
                            'namespace': namespace,
                            'error': f"Execution error: {error_msg}"
                        }
                    finally:
                        namespace.pop('__last_result', None)
            else:
                # Handle synchronous code
                with self._capture_output() as buffer:
                    try:
                        if is_last_line_expression:
                            # Separate the last expression from the rest
                            # Use all_lines to preserve indentation, find index of last non-comment line
                            last_non_empty_index = last_non_comment_index

                            main_statements = '\\n'.join(all_lines[:last_non_empty_index]) if last_non_empty_index > 0 else ''
                            last_expression = all_lines[last_non_empty_index].strip() if last_non_empty_index >= 0 else ''

                            # Execute statements first
                            if main_statements:
                                exec(main_statements, namespace)

                            # Execute and capture the last expression
                            # Note: eval() is intentional here — executes user Python code
                            last_result = eval(last_expression, namespace)

                            captured_output = buffer.getvalue()
                            if captured_output:
                                result['output'] += captured_output

                            # Skip void bridge call results (JsProxy wrapping a Promise)
                            from pyodide.ffi import JsProxy
                            is_void_bridge_result = (
                                isinstance(last_result, JsProxy) and
                                hasattr(last_result, 'then')
                            )
                            if not is_void_bridge_result:
                                result['last_expression_result'] = last_result
                                if last_result is not None:
                                    formatted_result = self._format_result_for_display(last_result)
                                    if captured_output:
                                        result['output'] += '\\n' + formatted_result
                                    else:
                                        result['output'] = formatted_result
                            elif not result['output']:
                                result['output'] = 'Code executed successfully'
                        else:
                            # No expression to capture, just execute
                            exec(main_code, namespace)
                            captured_output = buffer.getvalue()
                            if captured_output:
                                result['output'] += captured_output
                            elif not result['output']:
                                result['output'] = 'Code executed successfully'

                        result['namespace'] = namespace
                    except SystemExit as e:
                        return _handle_system_exit(e, buffer, namespace, result)
                    except Exception as e:
                        error_msg = format_error_message(e)
                        return {
                            'output': buffer.getvalue(),
                            'namespace': namespace,
                            'error': f"Execution error: {error_msg}"
                        }

            # Handle module context if present
            if context_id and not result.get('error'):
                context = self.get_module_context(context_id)
                if context:
                    module_obj = context['module_obj']
                    steps_pkg = context['steps_pkg']
                    module_id = context['id'].replace(f"{self._base_namespace}.", '')

                    # Update module namespace
                    module_obj.__dict__.update(result['namespace'])

                    # Update exports
                    exports = []
                    for key, value in result['namespace'].items():
                        if not key.startswith('_'):
                            if (inspect.isfunction(value) or
                                inspect.isclass(value) or
                                inspect.ismodule(value) or
                                not callable(value)):
                                exports.append(key)
                                setattr(module_obj, key, value)
                                setattr(getattr(steps_pkg, module_id), key, value)

                    result['exports'] = exports
                    self._exports[module_obj.__name__] = exports
                    self._executed_modules.add(module_obj.__name__)

                    # Copy variables to __main__ with function rebinding
                    import __main__
                    import types

                    for key, value in result['namespace'].items():
                        if not key.startswith('_'):  # Skip private variables
                            # Rebind functions to __main__ so they see REPL variable changes
                            if inspect.isfunction(value):
                                rebound_function = types.FunctionType(
                                    value.__code__,
                                    __main__.__dict__,  # Use __main__ as globals
                                    value.__name__,
                                    value.__defaults__,
                                    value.__closure__
                                )
                                __main__.__dict__[key] = rebound_function
                            else:
                                # Simple copy for non-functions
                                __main__.__dict__[key] = value

            return result

        except SystemExit as e:
            exit_code = e.code if e.code is not None else 0
            if exit_code != 0:
                return {
                    'output': '',
                    'namespace': namespace if 'namespace' in dir() else {},
                    'error': f"Execution terminated with exit code {exit_code}"
                }
            return {
                'output': 'Code executed successfully',
                'namespace': namespace if 'namespace' in dir() else {},
                'error': None
            }
        except Exception as e:
            # Create new buffer even for error reporting
            error_msg = format_error_message(e)
            with self._capture_output() as buffer:
                print(f"General execution error: {error_msg}")
                return {
                    'output': buffer.getvalue(),
                    'namespace': namespace,
                    'error': f"General execution error: {error_msg}"
                }

    async def import_module(self, module_name: str, requesting_module: str = None):
        try:
            # Ensure full qualified name
            if not module_name.startswith(f"{self._base_namespace}."):
                module_name = f"{self._base_namespace}.{module_name.lower()}"

            print(f"\\n=== Importing Module ===")
            print(f"Module name: {module_name}")

            if module_name not in sys.modules:
                raise ImportError(f"Module '{module_name}' not found in sys.modules")

            module_obj = sys.modules[module_name]

            # If already executed, just return the module
            if module_name in self._executed_modules:
                return {
                    'module': module_obj,
                    'exports': self._exports.get(module_name, []),
                    'error': None
                }

            # Add to dependencies if requesting module is provided
            if requesting_module:
                if requesting_module not in self._dependencies:
                    self._dependencies[requesting_module] = set()
                self._dependencies[requesting_module].add(module_name)

            return {
                'module': module_obj,
                'exports': self._exports.get(module_name, []),
                'error': None
            }

        except Exception as e:
            error_msg = f"Import failed: {format_error_message(e)}"
            print(error_msg)
            return {
                'module': None,
                'exports': [],
                'error': error_msg
            }

    def _update_module_exports(self, module_name: str, namespace: dict):
        print(f"\\n=== Updating Exports for {module_name} ===")
        exports = []
        print(f"Namespace keys: {list(namespace.keys())}")

        for attr_name, attr_value in namespace.items():
            if not attr_name.startswith('_'):
                if (inspect.isfunction(attr_value) or
                    inspect.isclass(attr_value) or
                    inspect.ismodule(attr_value) or
                    not callable(attr_value)):
                    exports.append(attr_name)
                    print(f"Added export: {attr_name} ({type(attr_value)})")

        self._exports[module_name] = exports
        print(f"Final exports: {exports}")
        print("=== Export Update Complete ===\\n")

    def check_circular_imports(self, module_name: str, checking_path: set = None):
        """
        Check for circular dependencies starting from a given module.
        """
        if checking_path is None:
            checking_path = set()

        if module_name in checking_path:
            return True  # Circular dependency found

        checking_path.add(module_name)

        for dep in self._dependencies.get(module_name, set()):
            if self.check_circular_imports(dep, checking_path):
                return True

        checking_path.remove(module_name)
        return False

    def setup_base_namespace(self, namespace=None):
        """
        Set up a base namespace with essential builtins and modules.
        Ensures all registered modules are available.
        """
        if namespace is None:
            namespace = {}

        # Ensure data in globals is a DataObject for dot notation access
        # This wraps the data dict in-place so GUI modifications are preserved
        if 'data' in globals():
            if not isinstance(globals()['data'], DataObject):
                # Wrap existing data dict with DataObject in globals
                globals()['data'] = DataObject(globals()['data'])
        else:
            # Initialize empty DataObject if no data exists
            globals()['data'] = DataObject({})

        # Add essential items to namespace
        namespace.update({
            '__builtins__': __builtins__,
            'asyncio': asyncio,
            'data': globals()['data']  # Reference the same object, not a copy
        })

        # Add bridge-registered globals from the Pyodide bridge (__main__) or
        # this module's globals into the step execution namespace.
        #
        # getJobArguments has always been injected this way. BOX/box and EHQ/ehq
        # are bridge-registered service handlers (see PythonEHQBridge.registerJsExternalModules
        # in python-ehq-bridge.helper.ts:755), which uses two complementary calls:
        #   1. python.globals.set(name, handler)  -- adds to __main__.__dict__ immediately
        #   2. python.registerJsModule(name, ...) -- makes the name importable, but does
        #      NOT populate sys.modules until the first explicit import happens
        # So at step-execution time, the handlers are reliably visible on __main__.__dict__,
        # while sys.modules only contains them if a previous step (or this step's code)
        # already triggered an explicit import of BOX or EHQ. The sys.modules sweep below
        # therefore can't be relied on for first-call resolution -- we have to fish from
        # __main__ explicitly. Without this injection, step code that references BOX/EHQ
        # raises NameError because the step's exec namespace is a fresh dict that does
        # not inherit from __main__.
        _main = sys.modules.get('__main__')
        for _bridge_name in self._BRIDGE_INJECTED_NAMES:
            _bridge_val = None
            if _main is not None and getattr(_main, '__dict__', None) and _bridge_name in _main.__dict__:
                _bridge_val = _main.__dict__[_bridge_name]
            elif _bridge_name in globals():
                _bridge_val = globals()[_bridge_name]
            if _bridge_val is not None:
                namespace[_bridge_name] = _bridge_val

        # Add all registered modules to namespace
        for module_id, module in self._modules.items():
            if module_id in self._executed_modules:
                namespace[module_id] = module
                # Also add by the module's actual name
                if hasattr(module, '__name__'):
                    namespace[module.__name__] = module

        # Add all non-private modules from sys.modules
        for module_name, module in sys.modules.items():
            if not module_name.startswith('_'):
                namespace[module_name] = module

        return namespace

    def get_module_dependencies(self, module_name: str) -> list:
        """Get all dependencies for a module."""
        return list(self._dependencies.get(module_name, set()))

    def get_module_exports(self, module_id: str) -> list:
        """Get list of exports for a module."""
        return self._exports.get(module_id, [])

    def diagnose_system_state(self):
        print("\\n=== System State Diagnostic ===")
        print(f"Base namespace: {self._base_namespace}")
        print(f"\\nRegistered Modules:")
        for module_id, module in self._modules.items():
            print(f"\\nModule: {module_id}")
            print(f"  __name__: {getattr(module, '__name__', 'N/A')}")
            print(f"  __package__: {getattr(module, '__package__', 'N/A')}")
            print(f"  Attributes: {dir(module)}")
            print(f"  Is executed: {module_id in self._executed_modules}")
            print(f"  Exports: {self._exports.get(module_id, [])}")

        print(f"\\nNamespace Package State:")
        if self._base_namespace in sys.modules:
            ns_pkg = sys.modules[self._base_namespace]
            print(f"  __all__: {getattr(ns_pkg, '__all__', [])}")
            print(f"  Attributes: {dir(ns_pkg)}")
        else:
            print("  Namespace package not registered")

        print("=== Diagnostic Complete ===\\n")

    def _cleanup_module(self, module_id: str):
        try:
            # Clean up from steps package
            if self._base_namespace in sys.modules:
                steps_pkg = sys.modules[self._base_namespace]
                clean_name = module_id.replace(f"{self._base_namespace}.", '')
                if hasattr(steps_pkg, clean_name):
                    delattr(steps_pkg, clean_name)
                if clean_name in getattr(steps_pkg, '__all__', []):
                    steps_pkg.__all__.remove(clean_name)

            # Remove from sys.modules
            if module_id in sys.modules:
                del sys.modules[module_id]

            # Clean internal storage
            if module_id in self._modules:
                del self._modules[module_id]
            if module_id in self._module_code:
                del self._module_code[module_id]
            if module_id in self._exports:
                del self._exports[module_id]
            if module_id in self._executed_modules:
                self._executed_modules.remove(module_id)
            if module_id in self._dependencies:
                del self._dependencies[module_id]
        except Exception as e:
            print(f"Warning: Error during module cleanup: {str(e)}")

# Create global instance
module_system = UnifiedPythonSystem("${this.baseNamespace}")
`;

    try {
      await this.pythonRuntime.runPythonAsync(moduleSystemSetup);
    } catch (error) {
      this.toastr.error('Failed to initialize Python system', 'Initialization Error');
      throw error;
    }
  }

  async registerModule(name: string, code: string, context?: ChunkContext): Promise<boolean> {
    const moduleId = this.sanitizeModuleName(name);

    try {
      const existingModule = this.moduleRegistry.get(moduleId);
      const registrationCode = `await module_system.register_module(${JSON.stringify(moduleId)}, ${JSON.stringify(moduleId)}, ${JSON.stringify(code)})`;
      const registration = await this.pythonRuntime.runPythonAsync(registrationCode);

      this.moduleRegistry.set(moduleId, {
        id: moduleId,
        name: name,
        code: code.trim(),
        namespace: registration.namespace,
        isLoaded: registration.is_loaded,
        lastUsed: Date.now(),
        useCount: existingModule ? existingModule.useCount : 0,
        dependencies: [],
        exports: registration.exports
      });

      // Journal the module registration for cleanup
      if (this.fileJournal) {
        this.fileJournal.logFileCreation(`${STEPS_NAMESPACE}.${moduleId}`, 'python-module');
      }

      return true;
    } catch (error) {
      const errorMessage = `Failed to register module ${name}: ${error}`;

      if (context) {
        context.addMessage(errorMessage, 'danger');
      }

      throw error;
    }
  }

  async importModule(moduleName: string, context?: ChunkContext, requestingModule?: string): Promise<any> {
    try {
      const moduleId = this.sanitizeModuleName(moduleName);
      const registryEntry = this.moduleRegistry.get(moduleId);

      if (!registryEntry) {
        throw new Error(`Module '${moduleName}' not found`);
      }

      const importCode = `
        async def do_import():
            result = await module_system.import_module(
                "${moduleId}",
                ${requestingModule ? `"${requestingModule}"` : 'None'}
            )
            print(f"Import result: {result}")
            return result

        await do_import()
      `;

      const result = await this.pythonRuntime.runPythonAsync(importCode);

      if (result.error) {
        throw new Error(result.error);
      }

      // Update registry
      registryEntry.lastUsed = Date.now();
      registryEntry.useCount += 1;
      registryEntry.exports = result.exports;
      this.moduleRegistry.set(moduleId, registryEntry);

      return result.module;
    } catch (error) {
      const errorMessage = `Failed to import module ${moduleName}: ${error}`;
      if (context) {
        context.addMessage(errorMessage, 'danger');
      }
      throw error;
    }
  }

  async diagnoseSystemState(): Promise<void> {
    try {
      await this.pythonRuntime.runPythonAsync(
        'module_system.diagnose_system_state()'
      );
    } catch (error) {
      console.warn('Python module manager: diagnostics failed', error);
    }
  }

  private generateModuleId(name: string): string {
    const sanitizedName = this.sanitizeModuleName(name);
    return `${sanitizedName}_${this.moduleCounter++}`;
  }

  public sanitizeModuleName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_{2,}/g, '_')  // Replace multiple consecutive underscores with single underscore
      .replace(/^[^a-z]/, 'mod_$&');
  }

  /**
   * Public method to analyze dependencies in Python code
   * Extracts steps.* imports from Python code, ignoring standard library and third-party imports
   */
  analyzeDependencies(code: string): DependencyAnalysis {
    const dependencies: string[] = [];
    const missingDependencies: string[] = [];

    if (!code) {
      return { dependencies, missingDependencies };
    }

    // Standard library modules to ignore
    const _standardLibraryModules = new Set([
      'sys', 'os', 'json', 'math', 'datetime', 'time', 'random', 're', 'collections',
      'itertools', 'functools', 'operator', 'string', 'array', 'struct', 'copy',
      'pickle', 'io', 'pathlib', 'shutil', 'glob', 'fnmatch', 'linecache',
      'stat', 'tempfile', 'fileinput', 'csv', 'configparser', 'netrc', 'xdrlib',
      'plistlib', 'hashlib', 'hmac', 'secrets', 'uuid', 'base64', 'binascii',
      'codecs', 'unicodedata', 'stringprep', 'readline', 'rlcompleter', 'ast',
      'symtable', 'symbol', 'token', 'tokenize', 'keyword', 'parser', 'dis',
      'pickletools', 'trace', 'traceback', 'gc', 'inspect', 'site', 'code',
      'codeop', 'py_compile', 'compileall', 'importlib', 'pkgutil', 'modulefinder',
      'runpy', 'zipimport', 'pkgutil', 'pydoc', 'doctest', 'unittest', 'test',
      'lib2to3', 'typing', 'types', 'warnings', 'contextlib', 'abc', 'atexit',
      'traceback', 'future_builtins', 'builtins', '__builtin__', '__future__'
    ]);

    // Regular expressions to match different import patterns
    // Uses baseNamespace ("steps") from STEPS_NAMESPACE constant
    const ns = this.baseNamespace;
    // Match: from steps.X import Y (including import *)
    const fromImportRegex = new RegExp(`from\\s+${ns}\\.(\\w+(?:\\.\\w+)*)\\s+import`, 'g');
    // Match: import steps.X (including "as Y")
    const importRegex = new RegExp(`import\\s+${ns}\\.(\\w+(?:\\.\\w+)*)(?:\\s+as\\s+\\w+)?`, 'g');
    // Match: from steps import X, Y, Z (standard Python package import)
    const fromNsImportRegex = new RegExp(`from\\s+${ns}\\s+import\\s+(.+)`, 'g');

    let match;
    // Extract from steps.X imports (bare name without "steps." prefix)
    while ((match = fromImportRegex.exec(code)) !== null) {
      const moduleName = match[1];
      if (!dependencies.includes(moduleName)) {
        dependencies.push(moduleName);
      }
    }

    // Extract import steps.X imports (bare name without "steps." prefix)
    while ((match = importRegex.exec(code)) !== null) {
      const moduleName = match[1];
      if (!dependencies.includes(moduleName)) {
        dependencies.push(moduleName);
      }
    }

    // Extract from steps import X, Y imports
    while ((match = fromNsImportRegex.exec(code)) !== null) {
      const names = match[1].split(',').map((n: string) => n.trim().split(' as ')[0].trim());
      for (const name of names) {
        if (name && /^\w+$/.test(name) && !dependencies.includes(name)) {
          dependencies.push(name);
        }
      }
    }

    // Check which dependencies are missing (not in registry)
    for (const dep of dependencies) {
      const moduleId = this.sanitizeModuleName(dep);
      if (!this.moduleRegistry.has(moduleId)) {
        missingDependencies.push(dep);
      }
    }

    return {
      dependencies,
      missingDependencies
    };
  }

  private async validateDependencies(
    dependencies: string[],
    context?: ChunkContext
  ): Promise<boolean> {
    for (const dep of dependencies) {
      if (!this.moduleRegistry.has(dep)) {
        const error = `Missing dependency: ${dep}`;
        if (context) {
          context.addMessage(error, 'danger');
        }
        throw new Error(error);
      }
    }
    return true;
  }

  // Phase 2: extractDependencies removed - functionality is duplicated
  // Use existing importsHelper.analyzeImports() for dependency analysis

  async updateModule(
    moduleId: string,
    newCode: string,
    context: ChunkContext
  ): Promise<boolean> {
    try {
      const entry = this.moduleRegistry.get(moduleId);
      if (!entry) {
        throw new Error(`Module ${moduleId} not found`);
      }

      // Unload existing module
      await this.pythonRuntime.runPythonAsync(
        `module_manager.unload_module("${moduleId}")`
      );

      // Register updated module
      const success = await this.registerModule(entry.name, newCode, context);

      if (!success) {
        // Rollback to old version on failure
        await this.registerModule(entry.name, entry.code, context);
        throw new Error('Module update failed, rolled back to previous version');
      }

      return success;

    } catch (error) {
      const errorMessage = `Failed to update module ${moduleId}: ${error}`;
      if (context) {
        context.addMessage(errorMessage, 'danger');
      }
      throw error;
    }
  }

  getModuleInfo(moduleId: string): ModuleRegistryEntry | undefined {
    return this.moduleRegistry.get(moduleId);
  }

  listModules(): Array<{ id: string, name: string, exports: string[] }> {
    return Array.from(this.moduleRegistry.values())
      .map(({ id, name, exports }) => ({ id, name, exports }));
  }

  async validateModuleDependencies(dependencies: string[], context?: ChunkContext): Promise<boolean> {
    try {
      const missingDeps: string[] = [];

      // Check all dependencies exist
      for (const dep of dependencies) {
        const moduleId = this.sanitizeModuleName(dep);
        if (!this.moduleRegistry.has(moduleId)) {
          missingDeps.push(dep);
        }
      }

      // If any dependencies are missing, show warning and return false
      if (missingDeps.length > 0) {
        const errorMessage = `Missing dependencies: ${missingDeps.join(', ')}`;
        if (this.toastr) {
          this.toastr.warning(errorMessage, 'Dependency Warning');
        }
        if (context) {
          context.addMessage(errorMessage, 'warning');
        }
        return false;
      }

      // Check for circular dependencies (if pythonRuntime is available)
      if (this.pythonRuntime) {
        try {
          for (const dep of dependencies) {
            const moduleId = this.sanitizeModuleName(dep);
            const checkCode = `module_system.check_circular_imports("${moduleId}")`;
            const hasCircular = await this.pythonRuntime.runPythonAsync(checkCode);
            if (hasCircular) {
              const errorMessage = `Circular dependency detected involving module: ${dep}`;
              if (context) {
                context.addMessage(errorMessage, 'danger');
              }
              return false;
            }
          }
        } catch (error) {
          console.warn('Python module manager: circular dependency check failed', error);
        }
      }

      return true;
    } catch (_error) {
      const errorMessage = `Dependency validation failed: ${_error}`;
      if (context) {
        context.addMessage(errorMessage, 'danger');
      }
      return false;
    }
  }
}

